import React from "react";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import { useQuery } from "@apollo/client";
import { useLogout } from "@app.automotus.io/components/auth";
import { useUserProfile } from "@app.automotus.io/components/hooks";
import { NavBarTarget } from "@app.automotus.io/components/NavBar";
import { GET_ALL_INVOICES, GetAllInvoicesData } from "common/graphql";

/** Builds the NavBar target list for the authenticated curbpass dashboard shell. */
export const useDashboardNavLinks = (): NavBarTarget[] => {
  const logout = useLogout();
  const { userProfile } = useUserProfile();
  // Reuses the query the shared NavBar already runs (Apollo cache dedupe, no extra request).
  // Fails open on error so a transient GraphQL error doesn't hide a tab the account may need.
  const { data: invoicesData, error: invoicesError } = useQuery<GetAllInvoicesData>(GET_ALL_INVOICES);
  const hasInvoices = invoicesError ? true : (invoicesData?.invoices?.length ?? 0) > 0;

  if (!userProfile) {
    return [];
  }

  return [
    {
      title: "Transactions",
      to: "/dashboard",
    },
    {
      title: "Permits",
      to: "/permits",
    },
    {
      title: "Vehicles",
      to: "/vehicles",
    },
    ...(hasInvoices
      ? [
          {
            title: "Invoices",
            to: "/invoices",
            icon: <KeyboardArrowRight sx={{ mr: -0.5 }} />,
          },
        ]
      : []),
    {
      // No onClick/new-tab override here: Support now opens in-app in the same tab, not an external redirect.
      title: "Support",
      to: "/support",
    },
    {
      title: "Account",
      to: "/account",
    },
    {
      title: "Logout",
      to: "/",
      onClick: async (e: React.SyntheticEvent) => {
        e.preventDefault();

        await logout();
      },
    },
  ];
};

export default useDashboardNavLinks;
